import { captureException } from "@sentry/react";
import { AxiosError, AxiosResponse, CancelTokenSource, default as axios, default as defaultAxios, ResponseType } from "axios";
import Cookies from "js-cookie";
// Imported from the specific file rather than the "../actions" barrel: that barrel re-exports
// every action module including ones that import back from this file (e.g. annotationsApi.ts ->
// utils.ts), which formed a circular import and intermittently threw
// "Cannot access 'secondsDelay' before initialization" wherever this module's own top-level
// exports hadn't finished evaluating yet.
import { setCanAccessView } from "../actions/pageActions";
import { __local__, __v2Url__ } from "../constants";
import type { store as StoreType } from "../store";

// store is only referenced inside handleError below, at error-handling time rather than at
// module load time. A normal top-level import here forms a second circular dependency: this
// module -> store -> root saga -> saga files (e.g. parameterSaga.ts) that import secondsDelay
// back from this same module, which throws "Cannot access 'secondsDelay' before initialization"
// since this module hasn't finished evaluating yet. Requiring it lazily breaks the cycle without
// changing handleError's synchronous signature or any call site.
const getStore = (): typeof StoreType => require("../store").store;

export const API_V2_URL = __v2Url__ || "https://dev-api.nissan-cedar.com/";
export const API_V2_NEW_URL = __v2Url__ || "https://dev-api.nissan-cedar.com/";

export type defaultApiInt = Promise<Record<string, any>>;
export type anyApiInt = Promise<any>;

export const CancelRequestMessage = "Operation cancelled due to a new request";
export const CANCEL_TOKENS: Record<string, CancelTokenSource> = {};

// Cancels any in-flight request under this key and issues a fresh cancel token for the new one -
// every fetch/post/update helper below needs this same cancel-and-replace step before firing.
const refreshCancelToken = (key: string): CancelTokenSource => {
  if (key in CANCEL_TOKENS) CANCEL_TOKENS[key].cancel(CancelRequestMessage);
  CANCEL_TOKENS[key] = axios.CancelToken.source();
  return CANCEL_TOKENS[key];
};

//Seconds delay between function retries
// Re-fetch data after 4sec
export const secondsDelay = 4000 + Math.floor(Math.random() * 150) + 1;

export const apiParamsToObject = (params: string) => {
  const parameters = new URLSearchParams(params);
  const regionParameter = parameters.get("region");

  if (regionParameter === "ASEAN/Japan") return { ...Object.fromEntries(parameters.entries()), region: "ASEAN" };

  return { ...Object.fromEntries(parameters.entries()) };
};

export const simpleResponse = (response: AxiosResponse) => response;

export const handleResponse = (response: AxiosResponse) => response.data ?? null;

// FastAPI/Pydantic 422s put a real array of {loc, msg, type} objects in `detail` (not a string) -
// e.g. { "detail": [{ "loc": ["body", "email"], "msg": "value is not a valid email address",
// "type": "value_error.email" }] }. Before this, `errorObj.error` below was just set to
// `responseData.detail` as-is - a plain string `detail` (the sentinel values this file's own
// `expired_token` check and callers like Login.tsx's `switch (response.error)` compare against)
// passed through fine, but an ARRAY detail was handed to callers unchanged, and every call site
// that renders `error` directly as text (e.g. LoginForm.tsx's `<p>{error}</p>`) crashed - React
// can't render an array of plain objects as a child - which is what actually produced the generic
// Sentry ErrorBoundary fallback ("Error: Something went wrong") instead of the real validation
// message. Flattens any {loc, msg} entries into one human-readable string; returns undefined for
// anything else so callers keep falling back to response.message exactly as before.
export const normalizeErrorDetail = (detail: unknown): string | undefined => {
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => {
        if (!entry || typeof entry !== "object" || typeof entry.msg !== "string") return undefined;
        // loc is usually ["body", "<field>"] - "body" is just where FastAPI looked, not part of
        // the field name a user would recognize.
        const field = Array.isArray(entry.loc) ? entry.loc.filter((part: unknown) => part !== "body").join(".") : undefined;
        return field ? `${field}: ${entry.msg}` : entry.msg;
      })
      .filter((msg): msg is string => Boolean(msg));

    return messages.length > 0 ? messages.join("; ") : undefined;
  }

  return undefined;
};

// Shared by buildErrorObj's reactive "a request just came back 401 expired_token" path below and
// useSessionExpiryWarning's proactive "the countdown reached zero" path - same end state either
// way (no valid token left), so both should clear it out and reload the same way.
export const forceLogoutForExpiredToken = () => {
  const cookies = Cookies.get();
  for (const cookie in cookies) {
    Cookies.remove(cookie);
  }
  localStorage.setItem("expiredToken", "true");
  localStorage.removeItem("LAST_VISITED_DASHBOARD");
  location.reload();
};

// Extracted from handleError below so the same detail-extraction/expired-token/403 logic can run
// either synchronously (the common case) or after a blob-responseType error body has first been
// read back out as text and reparsed as JSON (see handleError's own comment on why that's needed).
const buildErrorObj = (status: number | undefined, responseData: Record<string, unknown>, fallbackMessage: string) => {
  const hasDetail = Object.prototype.hasOwnProperty.call(responseData, "detail");

  const errorObj = {
    error: (hasDetail ? normalizeErrorDetail(responseData.detail) : undefined) ?? fallbackMessage,
    status: status,
  };

  // HACK: logout users if token has expired
  // TODO: implement refresh tokens
  //
  // Guarded on an access_token cookie actually being present - "expired_token" is the backend's
  // generic JWT-expiry sentinel (TokenService.decode_access_token, cedar-backend), reused as-is
  // for the UNRELATED password-reset/set-password token check (POST
  // /authentication/check-token-validity, called via postDataNoTokenV2 - no session cookie
  // involved at all). Without this guard, a visitor on the public /set-password page with an
  // expired reset link triggered this same forced logout-and-reload - wiping SetPasswordForm.tsx's
  // own `error` state (and the informative message it was about to show) before React ever got a
  // chance to render it, and reloading straight back into the same now-doomed request. An actual
  // expired SESSION always has a real (if now-invalid) access_token cookie to clear; an anonymous
  // password-reset visitor never had one to begin with.
  if (errorObj.error == "expired_token" && Cookies.get("access_token")) {
    forceLogoutForExpiredToken();
  }

  if (status == 403) {
    getStore().dispatch(setCanAccessView(false));
  }

  // A 401 is always "you're not currently authenticated/authorized" - either a login attempt with
  // the wrong credentials (LoginForm.tsx's own dedicated "Invalid credentials..." message) or an
  // expired session (the logout-and-reload branch above) - never itself a sign of an application
  // defect, just like the already-handled 403 above isn't. Reporting it to Sentry anyway meant
  // every ordinary mistyped password showed up there as a generic, uninformative "Object captured
  // as exception with keys: error, status" - real noise crowding out real bugs, not a signal
  // anyone could act on.
  if (status !== 401) {
    captureException(errorObj);
  }
  return errorObj;
};

export const handleError = (response: AxiosError) => {
  const isCancelError = response.message === CancelRequestMessage;

  if (!isCancelError) {
    if (localStorage.getItem("expiredToken") == "true") {
      return null;
    }

    const status = response.response?.status;

    // Safely handle cases where response.response or response.response.data may be null/undefined
    const responseData = (response.response && response.response.data ? response.response.data : {}) as Record<string, unknown>;

    // A blob-responseType request (e.g. exportDashboardAnalysis's CSV export) gets its ENTIRE
    // response body deserialized as a Blob regardless of status code - axios applies the
    // request's own responseType uniformly to whatever bytes come back, success or error alike.
    // A real backend detail message (e.g. "Query param 'export' is required for this dashboard
    // page. Options: ['channel_mix', 'overall']" - the exact text exportDashboardAnalysis's own
    // self-correcting export-name discovery depends on, see AnalyticsPage.tsx's findExportOptions)
    // arrives here as an unparsed Blob instead of the {detail: ...} object every other caller
    // gets, so `hasOwnProperty(responseData, "detail")` below was always false for it - silently
    // falling back to axios's own generic "Request failed with status code 400" forever, for
    // every dashboard registered with more than one named export. Reads it back out as text and
    // reparses it as JSON first, so it reaches the exact same detail-extraction logic as any other
    // request's error body.
    if (responseData instanceof Blob) {
      return responseData
        .text()
        .then((text) => {
          try {
            return JSON.parse(text);
          } catch {
            return {};
          }
        })
        .then((parsedResponseData) => buildErrorObj(status, parsedResponseData, response.message));
    }

    return buildErrorObj(status, responseData, response.message);
  }
  // Cancelled request (a newer one under the same key superseded it, see refreshCancelToken above) -
  // null, not undefined: react-query explicitly disallows a query function resolving to undefined
  // ("Query data cannot be undefined"), which this was otherwise triggering on every page whose
  // effects fire a refetch that gets cancelled (e.g. React 18 Strict Mode's double-invoke in dev).
  return null;
};

const V2Config = {
  baseURL: API_V2_URL,
  headers: { "Content-Type": "application/json", "Access-Control-Allow-Origin": "*" },
};
const V2NewConfig = { baseURL: API_V2_NEW_URL, headers: { "Content-Type": "application/json", "Access-Control-Allow-Origin": "*" } };

// Reads the access_token cookie fresh on every request, instead of baking it into a
// static header at module load - a static header would keep using whatever token
// existed when this module was first evaluated, even after the cookie rotates.
const attachBearerTokenInterceptor = (client: ReturnType<typeof defaultAxios.create>) => {
  client.interceptors.request.use((config) => {
    config.headers.Authorization = `Bearer ${Cookies.get("access_token") ?? ""}`;
    return config;
  });
};

export const axiosNoBearerV2 = defaultAxios.create(V2Config);

export const axiosWithBearerV2 = defaultAxios.create(V2Config);
attachBearerTokenInterceptor(axiosWithBearerV2);

export const simpleFetchV2 = (url: string) => {
  const cancelToken = refreshCancelToken(url).token;

  return axiosWithBearerV2.get(url, { cancelToken }).then(simpleResponse).catch(handleError);
};

// NS-908: postDataZipFileV2 (an `arraybuffer`-responseType POST, once used by every CSV/Excel
// export download) is gone - every export endpoint now uploads the file to GCS and returns a
// plain JSON ExportDownloadOut (download_url/file_name/...) instead of streaming the file bytes
// directly, so a normal postDataV2 call is all any of its former callers need now. See
// utils/downloadExport.ts's own comment for the new download flow.

// V2
// tokenKey defaults to url (unchanged behavior for every existing caller) - pass a distinct one
// when the same endpoint is legitimately fetched independently by more than one component on the
// same page, each with their own distinct key. Without a distinct key,
// refreshCancelToken's cancel-in-flight-request-under-this-key behavior - meant
// for a single caller superseding its own earlier request (e.g. search-as-you-type) - instead
// cancels a totally unrelated caller's request just because it happens to hit the same URL,
// resolving that caller's response to null (see handleError's isCancelError branch) even though
// the request that "won" succeeded.
export const fetchDataV2 = (url: string, headers?: Record<string, any>, responseType?: ResponseType, tokenKey?: string): anyApiInt => {
  const cancelToken = refreshCancelToken(tokenKey ?? url).token;

  return axiosWithBearerV2.get(url, { cancelToken, headers: headers, responseType: responseType }).then(handleResponse).catch(handleError);
};

export const getDataNoTokenV2 = <T>(url: string, token = ""): Promise<T> =>
  !token
    ? axiosNoBearerV2.get(url).then(handleResponse).catch(handleError)
    : axiosNoBearerV2
      .get(url, { ...V2Config, headers: { ...V2Config.headers, Authorization: `Bearer ${token ? token : ""}` } })
      .then(handleResponse)
      .catch(handleError);

export const postDataNoTokenV2 = <T, U>(url: string, data: T, token = ""): Promise<U> =>
  !token
    ? axiosNoBearerV2.post(url, data).then(handleResponse).catch(handleError)
    : axiosNoBearerV2
      .post(url, data, { ...V2Config, headers: { ...V2Config.headers, Authorization: `Bearer ${token ? token : ""}` } })
      .then(handleResponse)
      .catch(handleError);

// responseType mirrors fetchDataV2's own param - pass "blob" for a binary response (e.g. a CSV/XLSX
// export stream), same reasoning as every other binary fetch in this file: a plain download link
// pointed straight at the backend wouldn't carry the bearer-token auth this needs.
export const postDataV2 = (url: string, data: Record<string, any>, tokenKey?: string, responseType?: ResponseType): anyApiInt => {
  const cancelToken = refreshCancelToken(tokenKey ? tokenKey : url).token;

  return axiosWithBearerV2.post(url, data, { cancelToken, responseType }).then(handleResponse).catch(handleError);
};

export const updateDataV2 = (url: string, data: Record<string, any>): anyApiInt => {
  const cancelToken = refreshCancelToken(url).token;

  return axiosWithBearerV2.put(url, data, { cancelToken }).then(handleResponse).catch(handleError);
};

// A real PATCH (not PUT, unlike updateDataV2 above) - needed for the /admin/dashboard-categories/{id}
// and /admin/dashboard-tags/{id} endpoints (docs/needed-endpoints.md §11/§9), which are documented
// as PATCH specifically.
export const patchDataV2 = (url: string, data: Record<string, any>): anyApiInt => {
  const cancelToken = refreshCancelToken(url).token;

  return axiosWithBearerV2.patch(url, data, { cancelToken }).then(handleResponse).catch(handleError);
};

export const deleteDataV2 = (url: string): anyApiInt => axiosWithBearerV2.delete(url).then(handleResponse).catch(handleError);

export const newApiFetchV2 = <T>(url: string, data: Record<string, any>): anyApiInt =>
  __local__ ? fetchDataV2(url) : postDataV2(url, data);

//refactored endpoints
export const axiosWithBearerV2New = defaultAxios.create(V2NewConfig);
attachBearerTokenInterceptor(axiosWithBearerV2New);

export const newFetchDataV2 = (url: string, headers?: Record<string, any>, responseType?: ResponseType): anyApiInt => {
  const cancelToken = refreshCancelToken(url).token;

  return axiosWithBearerV2New
    .get(url, { cancelToken, headers: headers, responseType: responseType })
    .then(handleResponse)
    .catch(handleError);
};

export const newPostDataV2 = (url: string, data: Record<string, any>, tokenKey?: string): anyApiInt => {
  const cancelToken = refreshCancelToken(tokenKey ? tokenKey : url).token;

  return axiosWithBearerV2New.post(url, data, { cancelToken }).then(handleResponse).catch(handleError);
};

export const apiFetchV2 = <T>(url: string, data: Record<string, any>): anyApiInt =>
  __local__ ? newFetchDataV2(url) : newPostDataV2(url, data);
